import { VehicleImageWithMeta } from '@/catalog/types/vehicle';
import { createImageUrl } from '@/catalog/utils/pdp/create-image-url';
import Spacer from '@/common/components/spacer';
import { Button } from '@/shadcn-ui/button';
import { motion } from 'motion/react';
import HeroMissingImage from './hero-missing-image';
import ImagePreviewHeroGradient from './image-preview-hero-gradient';
import { findImagesByCategory } from '@/catalog/utils/pdp/find-images-by-category';
import { findFirstImageByCategory } from '@/catalog/utils/pdp/find-first-image-by-category';
import useAssembleThumbnails from '@/catalog/hooks/use-assemble-thumbnails';

export const createThumbnail = (
  imageUrl: string | undefined,
  altText: string,
  onClickDelegate: (image?: string) => void
) =>
  imageUrl ? (
    <ThumbnailButton
      imageUrl={imageUrl}
      altText={altText}
      onClick={() => onClickDelegate(imageUrl)}
    />
  ) : null;

interface ImagePreviewProps {
  interiorImages: VehicleImageWithMeta[];
  exteriorImages: VehicleImageWithMeta[];
  heroImage?: VehicleImageWithMeta;
  cdnUrl: string;
  /** Image passed to preview, otherwise show default if image undefined */
  onClickDelegate: (image?: string) => void;
}

interface ThumbnailButtonProps {
  imageUrl: string;
  altText: string;
  onClick: () => void;
}

const ThumbnailButton = ({ imageUrl, altText, onClick }: ThumbnailButtonProps) => (
  <Button variant="clear" onClick={onClick} className="h-full w-full object-cover">
    <motion.img
      src={imageUrl}
      alt={altText}
      fetchPriority="high"
      loading="eager"
      className="h-full w-full rounded-[16px] object-cover transition-all hover:shadow-shadow10"
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.1 }}
    />
  </Button>
);

export default function ImagePreview({
  interiorImages,
  exteriorImages,
  heroImage,
  onClickDelegate,
  cdnUrl,
}: ImagePreviewProps) {
  const heroImageUrl = heroImage ? createImageUrl(cdnUrl, heroImage) : undefined;
  const exteriorImageUrls = findImagesByCategory(exteriorImages, 'Exterior', {
    cdnUrl,
  });

  // Interior shots are a bit irregular, so we show the first 3 provided if they exist with the 4th as a fallback thumbnail for the 360 interior thumbnail.
  const interiorImageUrls = findImagesByCategory(interiorImages, 'Interior', {
    cdnUrl,
  });

  const interior360 = findFirstImageByCategory(interiorImages, '360int', {
    cdnUrl,
  });

  const thumbnailsAndViewAll = useAssembleThumbnails({
    exteriorImageUrls,
    interiorImageUrls,
    interior360,
    onClickDelegate,
  });

  return (
    <>
      {heroImageUrl ? (
        <Button
          variant="clear"
          onClick={() => onClickDelegate()}
          className="relative h-fit overflow-hidden"
        >
          <motion.img
            src={heroImageUrl}
            alt="Vehicle exterior front"
            fetchPriority="high"
            loading="eager"
            className="z-0 w-full -translate-y-[40px] transform object-cover"
            initial={{ scale: 1.5 }}
            whileTap={{ scale: 1.485 }}
            transition={{ duration: 0.1 }}
          />
          <ImagePreviewHeroGradient />
        </Button>
      ) : (
        <HeroMissingImage />
      )}
      <Spacer variant="height8" />
      <div className="flex w-full gap-[8px]">
        {/* Render shots and view all where we have them defined */}
        {thumbnailsAndViewAll[0] ? (
          <div className="w-1/3">{thumbnailsAndViewAll[0]}</div>
        ) : null}

        {thumbnailsAndViewAll[1] ? (
          <div className="w-1/3">{thumbnailsAndViewAll[1]}</div>
        ) : null}

        <div className="flex w-1/3 gap-[8px]">
          <div className="flex w-1/2 flex-col gap-[8px]">
            <div className="h-1/2">{thumbnailsAndViewAll[2] ?? null}</div>
            <div className="h-1/2">{thumbnailsAndViewAll[3] ?? null}</div>
          </div>
          <div className="flex w-1/2 flex-col gap-[8px]">
            <div className="h-1/2">{thumbnailsAndViewAll[4] ?? null}</div>
            <div className="h-1/2">{thumbnailsAndViewAll[5] ?? null}</div>
          </div>
        </div>
      </div>
    </>
  );
}
